import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, Search, Calendar, User, FileText, Edit, Eye, Link2, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import MailSendDialog from '../components/MailSendDialog';
import { desktopApi, mailFailureText, mailOutcomeToast, mailStatusText } from '../lib/desktopApi';

const PAGE_SIZE = 50;

const HistoryPage = () => {
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    agent_id: '',
    date_start: '',
    date_end: '',
    incident: '',
    floor: '',
    transfer: '',
  });
  const [mailPreview, setMailPreview] = useState(null);
  const [mailReportId, setMailReportId] = useState('');
  const [mailSending, setMailSending] = useState(false);

  useEffect(() => {
    fetchAgents();
    fetchReports();
  }, []);

  const fetchAgents = async () => {
    try {
      const data = await desktopApi.getAgents();
      setAgents(data);
    } catch (error) {
      console.error('Error fetching agents:', error);
    }
  };

  const fetchReports = async (nextOffset = 0) => {
    setLoading(true);
    try {
      const response = await desktopApi.getReports({
        agent_id: filters.agent_id || undefined,
        date_start: filters.date_start || undefined,
        date_end: filters.date_end || undefined,
        incident: filters.incident || undefined,
        floor: filters.floor || undefined,
        transfer: filters.transfer || undefined,
        limit: PAGE_SIZE,
        offset: nextOffset,
        withTotal: true,
      });
      setTotal(response.total);
      setOffset(nextOffset);
      setReports((prev) => (nextOffset === 0 ? response.items : [...prev, ...response.items]));
    } catch (error) {
      toast.error('Erreur lors du chargement des rapports');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    fetchReports(0);
  };

  const handleShare = async (reportId) => {
    try {
      const result = await desktopApi.createShareLink(reportId);
      try {
        await navigator.clipboard.writeText(result.url);
        toast.success('Lien de lecture copié', { description: result.url });
      } catch (error) {
        toast.message('Lien de lecture', { description: result.url });
      }
    } catch (error) {
      toast.error('Impossible de créer le lien de lecture');
    }
  };

  const openMailDialog = async (reportId) => {
    try {
      const preview = await desktopApi.previewReportMail(reportId);
      setMailReportId(reportId);
      setMailPreview(preview);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(mailFailureText(detail), { duration: 12000 });
    }
  };

  const confirmMail = async () => {
    if (!mailReportId) return;
    setMailSending(true);
    try {
      const result = await desktopApi.sendReportMail(mailReportId);
      mailOutcomeToast(toast, result?.mail);
      setMailPreview(null);
      fetchReports();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(mailFailureText(detail), { duration: 12000 });
    } finally {
      setMailSending(false);
    }
  };

  const handleDownloadPDF = async (reportId, dateStart) => {
    try {
      const { blob, filename } = await desktopApi.getReportDocument(reportId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename || `Rapport_Vatel_${dateStart.replace(/\./g, '-').replace(/\//g, '-')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      // Libérer le blob URL après le démarrage du téléchargement
      setTimeout(() => window.URL.revokeObjectURL(url), 5000);
      toast.success('Rapport téléchargé');
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  return (
    <div className="min-h-screen bg-background pt-[calc(1rem+var(--sat))] pl-[calc(1rem+var(--sal))] pr-[calc(1rem+var(--sar))] pb-[calc(10.5rem+env(safe-area-inset-bottom,0px))] md:pt-[calc(2.5rem+var(--sat))] md:pl-[calc(2.5rem+var(--sal))] md:pr-[calc(2.5rem+var(--sar))] md:pb-16">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/dashboard')}
            data-testid="back-button"
            className="flex min-h-11 items-center gap-2 text-muted-foreground hover:text-primary transition-colors mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour à l'accueil
          </button>
          <h1 className="text-3xl md:text-5xl tracking-tight uppercase font-bold mb-2">
            Historique des rapports
          </h1>
          <p className="text-muted-foreground">Consultez et téléchargez les rapports passés</p>
        </div>

        {/* Filtres */}
        <div className="bg-card border border-border rounded-lg p-6 mb-6">
          <h2 className="text-xl font-bold text-foreground mb-4 uppercase tracking-wide flex items-center gap-2">
            <Search className="w-5 h-5 text-primary" />
            Recherche
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="filter-agent">Agent</Label>
              <select
                id="filter-agent"
                value={filters.agent_id}
                onChange={(e) => setFilters(prev => ({ ...prev, agent_id: e.target.value }))}
                data-testid="filter-agent"
                className="flex h-11 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base min-[1025px]:text-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              >
                <option value="">Tous les agents</option>
                {agents.map(agent => (
                  <option key={agent.id} value={agent.id}>{agent.name}</option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="filter-date">Date</Label>
              <Input
                id="filter-date"
                type="text"
                placeholder="JJ/MM/AAAA"
                value={filters.date_start}
                onChange={(e) => setFilters(prev => ({ ...prev, date_start: e.target.value }))}
                data-testid="filter-date"
              />
            </div>
            <div>
              <Label htmlFor="filter-date-end">Date fin</Label>
              <Input
                id="filter-date-end"
                type="text"
                placeholder="JJ/MM/AAAA"
                value={filters.date_end}
                onChange={(e) => setFilters(prev => ({ ...prev, date_end: e.target.value }))}
                data-testid="filter-date-end"
              />
            </div>
            <div>
              <Label htmlFor="filter-incident">Incident</Label>
              <Input
                id="filter-incident"
                type="text"
                placeholder="Mot-clé incident"
                value={filters.incident}
                onChange={(e) => setFilters(prev => ({ ...prev, incident: e.target.value }))}
                data-testid="filter-incident"
              />
            </div>
            <div>
              <Label htmlFor="filter-floor">Étage / zone</Label>
              <Input
                id="filter-floor"
                type="text"
                placeholder="Ex: 2ème, réception..."
                value={filters.floor}
                onChange={(e) => setFilters(prev => ({ ...prev, floor: e.target.value }))}
                data-testid="filter-floor"
              />
            </div>
            <div>
              <Label htmlFor="filter-transfer">Transfert</Label>
              <select
                id="filter-transfer"
                value={filters.transfer}
                onChange={(e) => setFilters(prev => ({ ...prev, transfer: e.target.value }))}
                data-testid="filter-transfer"
                className="flex h-11 w-full rounded-md border border-input bg-card px-3 py-1 text-base min-[1025px]:text-sm text-foreground transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              >
                <option className="bg-card text-foreground" value="">Tous</option>
                <option className="bg-card text-foreground" value="yes">Avec transfert</option>
                <option className="bg-card text-foreground" value="no">Sans transfert</option>
              </select>
            </div>
            <div className="flex items-end">
              <button
                onClick={handleSearch}
                data-testid="btn-search"
                className="w-full h-11 px-8 rounded-md bg-primary hover:bg-primary/90 text-primary-foreground font-semibold transition-colors"
              >
                Rechercher
              </button>
            </div>
          </div>
        </div>

        {/* Liste des rapports */}
        <div className="space-y-4">
          {loading ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">Chargement...</p>
            </div>
          ) : reports.length === 0 ? (
            <div className="bg-card border border-border rounded-lg p-12 text-center">
              <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">Aucun rapport trouvé</p>
            </div>
          ) : (
            reports.map((report) => (
              <div
                key={report.id}
                className="group bg-card border border-border rounded-lg p-6 hover:border-primary/50 transition-all duration-200"
                data-testid={`report-item-${report.id}`}
              >
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Calendar className="w-4 h-4 text-primary" />
                      <h3 className="text-lg font-bold text-foreground">
                        {report.date_start} - {report.date_end}
                      </h3>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4" />
                        <span>
                          {report.agent_name}
                          {report.started_by_agent_name && report.started_by_agent_name !== report.agent_name && ` (commencé par ${report.started_by_agent_name})`}
                        </span>
                      </div>
                      {report.transfer_time && (
                        <div>
                          Transfert : {report.transfer_time}
                        </div>
                      )}
                      <div>
                        Service : {report.shift_start} – {report.shift_end}
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2" data-testid={`mail-status-${report.id}`}>
                      {mailStatusText(report.mail)}
                      {report.modified_after_send && report.mail?.status === 'sent' && (
                        <span className="ml-2 font-semibold text-amber-200" data-testid={`modified-after-send-${report.id}`}>Modifié après envoi</span>
                      )}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      onClick={() => navigate(`/rapport/${report.id}`)}
                      data-testid={`btn-view-${report.id}`}
                      className="flex items-center gap-2 h-11 px-4 rounded-md bg-secondary hover:bg-secondary/80 text-secondary-foreground font-medium transition-all duration-200"
                    >
                      <Eye className="w-4 h-4" />
                      Voir
                    </button>
                    <button
                      onClick={() => handleShare(report.id)}
                      data-testid={`btn-share-${report.id}`}
                      className="flex items-center gap-2 h-11 px-4 rounded-md bg-secondary hover:bg-secondary/80 text-secondary-foreground font-medium transition-all duration-200"
                    >
                      <Link2 className="w-4 h-4" />
                      Lien
                    </button>
                    {report.can_write && (
                    <button
                      onClick={() => navigate(`/report/edit/${report.id}`)}
                      data-testid={`btn-edit-${report.id}`}
                      className="flex items-center gap-2 h-11 px-4 rounded-md bg-secondary hover:bg-secondary/80 text-secondary-foreground font-medium transition-all duration-200"
                    >
                      <Edit className="w-4 h-4" />
                      Modifier
                    </button>
                    )}
                    {report.mail?.enabled !== false && (
                    <button
                      onClick={() => openMailDialog(report.id)}
                      data-testid={`btn-mail-${report.id}`}
                      className="flex items-center gap-2 h-11 px-4 rounded-md bg-secondary hover:bg-secondary/80 text-secondary-foreground font-medium transition-all duration-200"
                    >
                      <Mail className="w-4 h-4" />
                      {report.modified_after_send && report.mail?.status === 'sent' ? 'Renvoyer par e-mail' : 'Envoyer par e-mail'}
                    </button>
                    )}
                    <button
                      onClick={() => handleDownloadPDF(report.id, report.date_start)}
                      data-testid={`btn-download-${report.id}`}
                      className="flex items-center gap-2 h-11 px-4 rounded-md bg-primary hover:bg-primary/90 text-primary-foreground font-medium transition-all duration-200 hover:-translate-y-0.5 active:scale-95"
                    >
                      <Download className="w-4 h-4" />
                      Télécharger le PDF
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
          {!loading && reports.length < total && (
            <button
              type="button"
              onClick={() => fetchReports(offset + PAGE_SIZE)}
              data-testid="btn-more-reports"
              className="h-11 w-full rounded-md bg-secondary font-semibold text-secondary-foreground"
            >
              Voir la suite
            </button>
          )}
        </div>
      </div>
      <MailSendDialog
        open={!!mailPreview}
        preview={mailPreview}
        sending={mailSending}
        onCancel={() => { if (!mailSending) setMailPreview(null); }}
        onConfirm={confirmMail}
      />
    </div>
  );
};

export default HistoryPage;